import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, database, functions } from './firebase';
import { ref, get, onValue } from 'firebase/database';
import { httpsCallable } from 'firebase/functions';
import axios from 'axios';
import { useImpersonation } from './ImpersonationContext';

function TournamentsList() {
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  const [cuescoreUsername, setCuescoreUsername] = useState('');
  const [lastSync, setLastSync] = useState(null);
  const [completedCollapsed, setCompletedCollapsed] = useState(true); // Collapse completed by default
  const [finishingTournament, setFinishingTournament] = useState(null); // Track which tournament is being finished
  const [cleaningUp, setCleaningUp] = useState(false); // Track cleanup in progress
  
  // Impersonation context
  const { effectiveUserId } = useImpersonation();
  
  // Responsive helper - visual only
  const isMobile = window.innerWidth < 768;

  useEffect(() => {
    const loadData = async () => {
      const user = auth.currentUser;
      if (!user || !effectiveUserId) return;

      setLoading(true);

      // Load CueScore username for effective user
      const usernameRef = ref(database, `organisers/${effectiveUserId}/cuescoreUsername`);
      const usernameSnapshot = await get(usernameRef);
      if (usernameSnapshot.exists()) {
        setCuescoreUsername(usernameSnapshot.val());
      }

      // Load last sync time
      const syncRef = ref(database, `organisers/${effectiveUserId}/lastTournamentSync`);
      const syncSnapshot = await get(syncRef);
      if (syncSnapshot.exists()) {
        setLastSync(new Date(syncSnapshot.val()));
      }

      // Listen to tournaments in real-time
      const tournamentsRef = ref(database, `organisers/${effectiveUserId}/tournaments`);
      onValue(tournamentsRef, (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          const tournamentsList = Object.values(data).sort((a, b) => 
            new Date(b.starttime) - new Date(a.starttime)
          );
          
          // DEBUG: Check what data we're getting
          console.log('🔍 First tournament:', tournamentsList[0]);
          console.log('🔍 Player count field:', tournamentsList[0]?.playerCount);
          console.log('🔍 StatusCode field:', tournamentsList[0]?.statusCode, '(2 = Finished in CueScore)');
          
          // Log all completed tournaments and their statusCodes
          const completed = tournamentsList.filter(t => t.status === 'completed');
          console.log('🏁 Completed tournaments statusCodes:', completed.map(t => ({
            name: t.name,
            statusCode: t.statusCode,
            finishedInCueScore: t.statusCode === 2
          })));
          
          setTournaments(tournamentsList);
        } else {
          setTournaments([]);
        }
        setLoading(false);
      });
    };

    loadData();
  }, [effectiveUserId]);

  const handleSyncTournaments = async () => {
    if (!cuescoreUsername) {
      setError('Please link your CueScore account first');
      return;
    }

    setSyncing(true);
    setError('');

    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Not authenticated');
      }

      // Get Firebase ID token for authentication
      const idToken = await user.getIdToken();

      // Call Cloud Function
      const response = await axios.post(
        'https://us-central1-cue-clock-pro.cloudfunctions.net/fetchCueScoreTournaments',
        {
          cuescoreUsername: cuescoreUsername,
          userId: effectiveUserId
        },
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setLastSync(new Date());
        // Tournaments will update automatically via onValue listener
      } else {
        setError('Failed to sync tournaments');
      }
    } catch (err) {
      console.error('Error syncing tournaments:', err);
      setError('Failed to sync tournaments. Please try again.');
    } finally {
      setSyncing(false);
    }
  };

  const handleViewTournament = (tournament) => {
    // Navigate to tournament details page
    navigate(`/tournament/${tournament.tournamentId}`);
  };

  const handleFinishTournament = async (tournament) => {
    const user = auth.currentUser;
    if (!user) return;

    // Confirm with user
    const confirmed = window.confirm(
      `Are you sure you want to finish "${tournament.name}" in CueScore?\n\nThis will mark the tournament as completed.`
    );
    
    if (!confirmed) return;

    setFinishingTournament(tournament.tournamentId);
    setError('');

    try {
      const finishCueScoreTournament = httpsCallable(functions, 'finishCueScoreTournament');
      
      const result = await finishCueScoreTournament({
        tournamentId: tournament.tournamentId,
        organiserId: effectiveUserId,
        finishComment: ''
      });

      console.log('Tournament finished:', result.data);
      
      // Optionally trigger a sync to refresh the tournament list
      // handleSyncTournaments();
      
    } catch (err) {
      console.error('Error finishing tournament:', err);
      setError(`Failed to finish tournament: ${err.message}`);
    } finally {
      setFinishingTournament(null);
    }
  };

  const handleCleanupTournaments = async () => {
    const user = auth.currentUser;
    if (!user || !effectiveUserId) return;

    setCleaningUp(true);
    setError('');

    try {
      const cleanupTournaments = httpsCallable(functions, 'cleanupTournaments');
      const result = await cleanupTournaments({ userId: effectiveUserId });
      
      alert(`Cleanup complete: ${result.data.markedCompleted} marked completed, ${result.data.deleted} deleted`);
    } catch (err) {
      console.error('Error cleaning up tournaments:', err);
      setError(`Cleanup failed: ${err.message}`);
    } finally {
      setCleaningUp(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-GB', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'live': return '#ef4444';
      case 'upcoming': return '#3b82f6';
      case 'completed': return '#5a6478';
      default: return '#5a6478';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'live': return '🔴 LIVE';
      case 'upcoming': return '📅 Upcoming';
      case 'completed': return '✓ Completed';
      default: return status;
    }
  };

  const styles = {
    card: {
      backgroundColor: '#151c2e',
      borderRadius: '14px',
      padding: '24px',
      marginBottom: '24px',
      border: '1px solid #2a3555',
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
      fontFamily: "'DM Sans', sans-serif"
    },
    header: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      gap: isMobile ? '12px' : '0',
      justifyContent: 'space-between',
      alignItems: isMobile ? 'stretch' : 'center',
      marginBottom: '20px'
    },
    title: {
      fontSize: isMobile ? '18px' : '20px',
      fontWeight: '700',
      fontFamily: "'Outfit', sans-serif",
      letterSpacing: '-0.01em'
    },
    syncButton: {
      padding: '10px 20px',
      backgroundColor: '#3b82f6',
      color: 'white',
      border: 'none',
      borderRadius: '10px',
      fontSize: '14px',
      fontWeight: '600',
      fontFamily: "'DM Sans', sans-serif",
      cursor: 'pointer',
      boxShadow: '0 2px 8px rgba(59, 130, 246, 0.2)',
      transition: 'all 0.2s ease'
    },
    syncButtonDisabled: {
      padding: '10px 20px',
      backgroundColor: '#1c2640',
      color: '#5a6478',
      border: 'none',
      borderRadius: '10px',
      fontSize: '14px',
      fontWeight: '600',
      fontFamily: "'DM Sans', sans-serif",
      cursor: 'not-allowed'
    },
    lastSync: {
      fontSize: '12px',
      color: '#5a6478',
      marginBottom: '16px'
    },
    tournamentCard: {
      backgroundColor: '#1c2640',
      borderRadius: '10px',
      padding: '16px',
      marginBottom: '12px',
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      gap: isMobile ? '12px' : '0',
      justifyContent: 'space-between',
      alignItems: isMobile ? 'stretch' : 'center',
      border: '1px solid #2a3555',
      transition: 'border-color 0.2s ease'
    },
    tournamentInfo: {
      flex: 1
    },
    tournamentName: {
      fontSize: '17px',
      fontWeight: '600',
      marginBottom: '8px',
      fontFamily: "'Outfit', sans-serif"
    },
    tournamentMeta: {
      fontSize: '13px',
      color: '#8b95a8',
      display: 'flex',
      gap: '16px',
      flexWrap: 'wrap'
    },
    status: {
      display: 'inline-block',
      padding: '3px 10px',
      borderRadius: '9999px',
      fontSize: '11px',
      fontWeight: '600',
      letterSpacing: '0.02em'
    },
    createButton: {
      padding: '10px 20px',
      backgroundColor: '#10b981',
      color: 'white',
      border: 'none',
      borderRadius: '10px',
      fontSize: '14px',
      fontWeight: '600',
      fontFamily: "'DM Sans', sans-serif",
      cursor: 'pointer',
      marginLeft: '16px',
      boxShadow: '0 2px 8px rgba(16, 185, 129, 0.2)',
      transition: 'all 0.2s ease'
    },
    empty: {
      textAlign: 'center',
      padding: '40px',
      color: '#5a6478'
    },
    error: {
      padding: '12px 16px',
      backgroundColor: 'rgba(239, 68, 68, 0.12)',
      color: '#f87171',
      borderRadius: '10px',
      fontSize: '14px',
      marginBottom: '16px',
      border: '1px solid rgba(239, 68, 68, 0.2)'
    }
  };

  if (!cuescoreUsername) {
    return (
      <div style={styles.card}>
        <h2 style={styles.title}>🏆 CueScore Tournaments</h2>
        <div style={styles.empty}>
          Please link your CueScore account above to view your tournaments.
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <h2 style={styles.title}>🏆 CueScore Tournaments</h2>
        <div style={{display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '8px'}}>
          <button
            onClick={handleCleanupTournaments}
            style={cleaningUp ? styles.syncButtonDisabled : {...styles.syncButton, backgroundColor: '#d97706'}}
            disabled={cleaningUp}
          >
            {cleaningUp ? '🧹 Cleaning...' : '🧹 Cleanup'}
          </button>
          <button
            onClick={handleSyncTournaments}
            style={syncing ? styles.syncButtonDisabled : styles.syncButton}
            disabled={syncing}
          >
            {syncing ? '🔄 Syncing...' : '🔄 Sync Now'}
          </button>
        </div>
      </div>

      {lastSync && (
        <div style={styles.lastSync}>
          Last synced: {lastSync.toLocaleString()}
        </div>
      )}

      {error && <div style={styles.error}>⚠️ {error}</div>}

      {loading ? (
        <div style={styles.empty}>Loading tournaments...</div>
      ) : tournaments.length === 0 ? (
        <div style={styles.empty}>
          No tournaments found. Click "Sync Now" to fetch from CueScore.
        </div>
      ) : (
        <>
          {/* Active/Upcoming Tournaments Section */}
          <div>
            <h3 style={{fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#10b981', fontFamily: "'Outfit', sans-serif", letterSpacing: '0.02em', textTransform: 'uppercase'}}>
              Live & Upcoming Tournaments
            </h3>
            {tournaments.filter(t => t.status !== 'completed').length === 0 ? (
              <div style={{...styles.empty, padding: '20px'}}>
                No active or upcoming tournaments
              </div>
            ) : (
              tournaments
                .filter(t => t.status !== 'completed')
                .map((tournament) => (
                  <div key={tournament.tournamentId} style={styles.tournamentCard}>
                    <div style={styles.tournamentInfo}>
                      <div style={styles.tournamentName}>{tournament.name}</div>
                      <div style={styles.tournamentMeta}>
                        <span>
                          <span style={{ 
                            ...styles.status, 
                            backgroundColor: getStatusColor(tournament.status) 
                          }}>
                            {getStatusText(tournament.status)}
                          </span>
                        </span>
                        <span>📅 {formatDate(tournament.starttime)}</span>
                        <span>🕐 {formatTime(tournament.starttime)}</span>
                        <span>🎱 {tournament.discipline}</span>
                        {tournament.venues && tournament.venues.length > 0 && (
                          <span>📍 {tournament.venues[0].name || tournament.venues[0].venueName || 'Venue'}</span>
                        )}
                        <span>👥 {tournament.playerCount || tournament.participants?.length || 0} players</span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleViewTournament(tournament)}
                      style={styles.createButton}
                    >
                      🎯 View Matches
                    </button>
                  </div>
                ))
            )}
          </div>

          {/* Completed Tournaments Section - Collapsible */}
          {tournaments.filter(t => t.status === 'completed').length > 0 && (
            <div style={{marginTop: '32px'}}>
              <div 
                onClick={() => setCompletedCollapsed(!completedCollapsed)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  padding: '12px',
                  backgroundColor: '#1c2640',
                  borderRadius: '10px',
                  marginBottom: completedCollapsed ? '0' : '16px',
                  border: '1px solid #2a3555',
                  transition: 'all 0.2s ease'
                }}
              >
                <span style={{fontSize: '16px', fontWeight: '600', color: '#8b95a8', flex: 1, fontFamily: "'Outfit', sans-serif"}}>
                  Completed Tournaments ({tournaments.filter(t => t.status === 'completed').length})
                </span>
                <span style={{fontSize: '18px', color: '#5a6478'}}>
                  {completedCollapsed ? '▶' : '▼'}
                </span>
              </div>

              {!completedCollapsed && (
                <div style={{marginTop: '16px'}}>
                  {tournaments
                    .filter(t => t.status === 'completed')
                    .map((tournament) => (
                      <div key={tournament.tournamentId} style={styles.tournamentCard}>
                        <div style={styles.tournamentInfo}>
                          <div style={styles.tournamentName}>{tournament.name}</div>
                          <div style={styles.tournamentMeta}>
                            <span>
                              <span style={{ 
                                ...styles.status, 
                                backgroundColor: getStatusColor(tournament.status) 
                              }}>
                                {getStatusText(tournament.status)}
                              </span>
                            </span>
                            <span>📅 {formatDate(tournament.starttime)}</span>
                            <span>🕐 {formatTime(tournament.starttime)}</span>
                            <span>🎱 {tournament.discipline}</span>
                            {tournament.venues && tournament.venues.length > 0 && (
                              <span>📍 {tournament.venues[0].name || tournament.venues[0].venueName || 'Venue'}</span>
                            )}
                            <span>👥 {tournament.playerCount || tournament.participants?.length || 0} players</span>
                          </div>
                        </div>
                        <div style={{display: 'flex', gap: '8px'}}>
                          <button
                            onClick={() => handleViewTournament(tournament)}
                            style={{
                              ...styles.createButton,
                              backgroundColor: '#6b7280'
                            }}
                          >
                            📊 View Results
                          </button>
                          {/* Only show Finish button if NOT already finished in CueScore (statusCode !== 2) */}
                          {tournament.statusCode !== 2 ? (
                            <button
                              onClick={() => handleFinishTournament(tournament)}
                              disabled={finishingTournament === tournament.tournamentId}
                              style={{
                                ...styles.createButton,
                                backgroundColor: finishingTournament === tournament.tournamentId ? '#4b5563' : '#dc2626',
                                cursor: finishingTournament === tournament.tournamentId ? 'not-allowed' : 'pointer'
                              }}
                            >
                              {finishingTournament === tournament.tournamentId ? '⏳ Finishing...' : '🏁 Finish in CueScore'}
                            </button>
                          ) : (
                            <span style={{
                              padding: '10px 20px',
                              backgroundColor: '#374151',
                              color: '#10b981',
                              borderRadius: '8px',
                              fontSize: '14px',
                              fontWeight: 'bold',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}>
                              ✅ Finished in CueScore
                            </span>
                          )}
                        </div>
                      </div>
                    ))
                  }
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default TournamentsList;